import { Switch } from '@base-ui/react/switch';
import clsx from 'clsx';
import { type InputHTMLAttributes, type ReactNode, useId } from 'react';

import '@repo/sds/components/SDSSwitch/SDSSwitch.scss';

export interface SDSSwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  checked?: boolean;
  isDisabled?: boolean;
  isError?: boolean;
  isTabSelectable?: boolean;
  className?: string;
  label?: ReactNode;
  labelPosition?: 'left' | 'right';
  onChange?: () => void;
}

export function SDSSwitch({
  checked,
  isDisabled,
  isError,
  isTabSelectable = true,
  className,
  onChange,
  id,
  name,
  value,
  label,
  labelPosition = 'right',
  ...restProps
}: SDSSwitchProps) {
  const switchClass = clsx(
    'SDSSwitch',
    {
      'SDSSwitch--active': checked,
      'SDSSwitch--disabled': isDisabled,
      'SDSSwitch--error': isError,
    },
    className,
  );

  const generatedId = useId();
  const finalId = id || generatedId;
  const labelId = label ? `${finalId}-label` : undefined;

  const handleCheckedChange = () => {
    if (onChange) {
      onChange();
    }
  };

  // Extract compatible accessibility and data props
  const compatibleProps: Record<string, unknown> = {};
  if (name) compatibleProps.name = name;
  if (value) compatibleProps.value = value;
  if ('data-testid' in restProps) compatibleProps['data-testid'] = restProps['data-testid'];
  if ('aria-label' in restProps) compatibleProps['aria-label'] = restProps['aria-label'];

  // Handle aria-labelledby
  let ariaLabelledBy = restProps['aria-labelledby'] as string | undefined;
  if (labelId) {
    ariaLabelledBy = ariaLabelledBy ? `${labelId} ${ariaLabelledBy}` : labelId;
  }
  if (ariaLabelledBy) compatibleProps['aria-labelledby'] = ariaLabelledBy;

  if ('aria-describedby' in restProps) compatibleProps['aria-describedby'] = restProps['aria-describedby'];

  const switchElement = (
    <Switch.Root
      className={switchClass}
      checked={checked}
      disabled={isDisabled}
      tabIndex={isTabSelectable ? 0 : -1}
      onCheckedChange={handleCheckedChange}
      uncheckedValue="off"
      id={finalId}
      {...compatibleProps}
    >
      <Switch.Thumb className="SDSSwitch__thumb" />
    </Switch.Root>
  );

  if (label) {
    return (
      <label
        htmlFor={finalId}
        className={clsx('SDSSwitch__wrapper', {
          'SDSSwitch__wrapper--disabled': isDisabled,
          'SDSSwitch__wrapper--label-left': labelPosition === 'left',
        })}
      >
        {labelPosition === 'left' && (
          <span id={labelId} className="SDSSwitch__label">
            {label}
          </span>
        )}
        {switchElement}
        {labelPosition === 'right' && (
          <span id={labelId} className="SDSSwitch__label">
            {label}
          </span>
        )}
      </label>
    );
  }

  return switchElement;
}
